import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource/caveat/400.css";
import "@fontsource/caveat/600.css";
import "@fontsource/caveat/700.css";
import "@fontsource/kalam/400.css";
import "@fontsource/permanent-marker/400.css";
import "@fontsource/special-elite/400.css";
import "@fontsource-variable/fraunces";
import "@fontsource-variable/fraunces/wght-italic.css";
import "@fontsource-variable/shantell-sans";
import "@fontsource-variable/jetbrains-mono";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/canvas.css";
import "./styles/ui.css";
import "./styles/pages.css";
import { match, navigate, usePath } from "./app/router";
import { SessionProvider, useSession } from "./app/session";
import { ToastHost, Spinner } from "./ui/kit";
import { Landing } from "./pages/Landing";
import { AuthPage } from "./pages/Auth";
import { Library } from "./pages/Library";
import { BoardPage } from "./pages/BoardPage";
import { Account } from "./pages/Account";
import { listGuestBoards } from "./sync/localdb";
import { startGuestBoard } from "./pages/guest";
import { api } from "./sync/api";

function Home() {
  const { user } = useSession();
  useEffect(() => {
    if (user === null) {
      // signed out: show the landing page unless there are boards on this device
      listGuestBoards().then((g) => {
        if (!g.length) navigate("/welcome", { replace: true });
      });
    }
  }, [user]);
  if (user === undefined) return <div className="page-center"><Spinner /></div>;
  return <Library />;
}

function NewBoard() {
  const { user } = useSession();
  useEffect(() => {
    if (user === undefined) return;
    if (!user) {
      startGuestBoard();
      return;
    }
    api.createBoard({ title: "Untitled board" }).then((r) => navigate("/b/" + r.board.id, { replace: true })).catch(() => navigate("/", { replace: true }));
  }, [user]);
  return <div className="page-center"><Spinner label="Making a board" /></div>;
}

function Routes() {
  const path = usePath();
  let m: Record<string, string> | null;
  if (path === "/") return <Home />;
  if (path === "/welcome") return <Landing />;
  if (path === "/login") return <AuthPage mode="login" />;
  if (path === "/signup") return <AuthPage mode="signup" />;
  if (path === "/recover") return <AuthPage mode="recover" />;
  if (path === "/account") return <Account />;
  if (path === "/new") return <NewBoard />;
  if ((m = match("/b/:id", path))) return <BoardPage key={"b" + m.id} kind="cloud" id={m.id} />;
  if ((m = match("/g/:id", path))) return <BoardPage key={"g" + m.id} kind="guest" id={m.id} />;
  if ((m = match("/s/:token", path))) return <BoardPage key={"s" + m.token} kind="shared" id={m.token} />;
  return (
    <div className="page-center">
      <div className="lost-card">
        <img src="/art/empty-library.webp" alt="" width="300" />
        <h1>This page wandered off.</h1>
        <p>
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>Back to your boards</a>
        </p>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <SessionProvider>
      <ToastHost>
        <Routes />
      </ToastHost>
    </SessionProvider>
  </StrictMode>
);

// block Safari's page pinch-zoom outside the canvas, the canvas handles its own
document.addEventListener("gesturestart", (e) => e.preventDefault(), { passive: false });

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => navigator.serviceWorker.register("/sw.js").catch(() => undefined));
}
